import { Box, IconButton, Tooltip } from '@mui/material';
import {
  Star as StarIcon,
  StarBorder as StarBorderIcon,
} from '@mui/icons-material';
import { colors } from '@/theme/tokens';
import type { FavoriteTarget } from '@/hooks/use-device-favorites';

interface FavoriteStarProps {
  /** Hardware identifier. Empty for legacy rows — the star is hidden then. */
  machineId?: string;
  starred: boolean;
  /** False without `device:write`. Renders a read-only indicator instead. */
  canStar: boolean;
  /**
   * Display fields the caller already has. Passed straight through to the
   * optimistic update so a just-starred machine can appear in the sidebar
   * immediately instead of a request later.
   */
  meta?: FavoriteTarget;
  onToggle: (
    machineId: string,
    currentlyStarred: boolean,
    meta?: FavoriteTarget,
  ) => void;
  size?: number;
}

/**
 * The star, everywhere it appears.
 *
 * Three states, not two. Without `device:write` the toggle would 403 and roll
 * back under the user, so instead of a control that promises an action it cannot
 * perform, an existing star renders as a dimmed indicator and an unstarred row
 * renders nothing at all. A support engineer can still see what they starred
 * elsewhere; they just cannot change it here.
 */
export function FavoriteStar({
  machineId,
  starred,
  canStar,
  meta,
  onToggle,
  size = 17,
}: FavoriteStarProps) {
  // Favorites are keyed on the hardware id. A row without one (legacy, or
  // mid-enrollment) has nothing to key on, so there is no star to offer.
  if (!machineId) return null;

  if (!canStar) {
    if (!starred) return null;
    return (
      <Tooltip title="Starred. Changing favorites needs device write access." arrow>
        <StarIcon
          sx={{ fontSize: size, color: colors.primary.main, opacity: 0.55, display: 'block' }}
        />
      </Tooltip>
    );
  }

  return (
    <Tooltip title={starred ? 'Remove from favorites' : 'Add to favorites'} arrow>
      <IconButton
        size="small"
        aria-label={starred ? 'Remove from favorites' : 'Add to favorites'}
        aria-pressed={starred}
        onClick={(e) => {
          // The row is a link to device detail; starring must not navigate.
          e.stopPropagation();
          e.preventDefault();
          onToggle(machineId, starred, meta);
        }}
        sx={{
          p: 0.25,
          color: starred ? colors.primary.main : colors.border.light,
          '&:hover': {
            color: starred ? colors.primary.hover : colors.primary.light,
            bgcolor: 'transparent',
          },
        }}
      >
        {starred ? (
          <StarIcon sx={{ fontSize: size }} />
        ) : (
          <StarBorderIcon sx={{ fontSize: size }} />
        )}
      </IconButton>
    </Tooltip>
  );
}

/** Centred wrapper for the devices table's fixed-width leading column. */
export function FavoriteStarCell(props: FavoriteStarProps) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 24 }}>
      <FavoriteStar {...props} />
    </Box>
  );
}
